import React, { useMemo, useState } from 'react';
import { CheckCircle2, ChevronDown, Search } from 'lucide-react';
import { Input } from '../ui/input';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { StatusBadge } from './StatusBadge';
import { STATUS_ORDER, aud, fmtDate, freqLabel, slug } from './utils';

const FILTERS = ['All', 'Overdue', 'Payment Due', 'Follow Up Sent', 'Escalated', 'On Track'];
const HEADERS = ['Client', 'Original', 'Agreed payment', 'Deposit', 'Start', 'Paid', 'Balance', 'Last payment', 'Next due', 'Expected payoff', 'Status', 'Halaxy plan', 'Last checked', 'Invoice'];

export const DepositCell = ({ p }) => {
  if (p.deposit_met === null) return <span className="text-xs text-[#9B9590]">Pending sync</span>;
  return p.deposit_met ? (
    <span className="whitespace-nowrap text-xs font-medium text-[#4A6B2E]">{aud(p.deposit_required)} met ✓</span>
  ) : (
    <span className="whitespace-nowrap text-xs font-medium text-[#C17B6B]">{aud(p.deposit_paid)} of {aud(p.deposit_required)} · not met</span>
  );
};

const Row = ({ p, onOpen }) => (
  <tr onClick={() => onOpen(p)} className={`cursor-pointer border-t border-[#F0ECE4] transition-colors hover:bg-[#FDFBF7] ${p.needs_follow_up ? 'bg-[#FFF5F3]' : ''}`}
    data-testid={`plan-row-${p.id}`}>
    <td className="px-4 py-3">
      <div className="font-medium text-[#3A3530]">{p.client_name}</div>
      <div className="text-xs text-[#9B9590]">{p.assessment_type}{p.notes.length ? ` · ${p.notes.length} note${p.notes.length === 1 ? '' : 's'}` : ''}</div>
    </td>
    <td className="px-4 py-3 text-right tabular-nums text-[#6B6560]">{aud(p.original_balance)}</td>
    <td className="px-4 py-3 whitespace-nowrap">{aud(p.instalment_amount)} <span className="text-xs text-[#9B9590]">{freqLabel(p)}</span></td>
    <td className="px-4 py-3" data-testid={`plan-deposit-${p.id}`}><DepositCell p={p} /></td>
    <td className="px-4 py-3 whitespace-nowrap text-[#6B6560]">{fmtDate(p.start_date)}</td>
    <td className="px-4 py-3 text-right tabular-nums text-[#4A6B2E]">{aud(p.paid)}</td>
    <td className="px-4 py-3 text-right font-semibold tabular-nums">{aud(p.balance)}</td>
    <td className="px-4 py-3 whitespace-nowrap text-[#6B6560]" data-testid={`plan-last-payment-${p.id}`}>
      {p.last_payment_date ? <>{fmtDate(p.last_payment_date, { day: '2-digit', month: 'short' })} · <span className="font-medium text-[#4A6B2E]">{aud(p.last_payment_amount)}</span></> : '—'}
    </td>
    <td className={`px-4 py-3 whitespace-nowrap ${p.needs_follow_up ? 'font-semibold text-[#C0392B]' : ''}`} data-testid={`plan-next-due-${p.id}`}>{fmtDate(p.next_due)}</td>
    <td className="px-4 py-3 whitespace-nowrap text-[#6B6560]" data-testid={`plan-payoff-${p.id}`}>
      {fmtDate(p.expected_payoff_date)}
      {p.remaining_instalments > 0 && <div className="text-[11px] text-[#9B9590]">{p.remaining_instalments} payment{p.remaining_instalments === 1 ? '' : 's'} left</div>}
    </td>
    <td className="px-4 py-3"><StatusBadge status={p.status} testId={`plan-status-${p.id}`} /></td>
    <td className="px-4 py-3 text-center">{p.halaxy_plan_setup ? <CheckCircle2 className="mx-auto h-4 w-4 text-[#7BA05B]" /> : <span className="text-xs text-[#C17B6B]">No</span>}</td>
    <td className="px-4 py-3 whitespace-nowrap text-xs text-[#9B9590]">{fmtDate(p.last_checked_at, { day: '2-digit', month: 'short' })}</td>
    <td className="px-4 py-3 text-xs text-[#9B9590]">#{p.invoice_id}</td>
  </tr>
);

const Table = ({ rows, onOpen, testId }) => (
  <div className="overflow-x-auto" data-testid={testId}>
    <table className="w-full min-w-[1480px] text-sm">
      <thead>
        <tr className="text-left text-[11px] uppercase tracking-wider text-[#9B9590]">
          {HEADERS.map((h, i) => <th key={h} className={`px-4 py-2 font-semibold ${[1, 5, 6].includes(i) ? 'text-right' : ''}`}>{h}</th>)}
        </tr>
      </thead>
      <tbody>{rows.map((p) => <Row key={p.id} p={p} onOpen={onOpen} />)}</tbody>
    </table>
  </div>
);

const sortPlans = (a, b) =>
  STATUS_ORDER[a.status] - STATUS_ORDER[b.status] ||
  (a.overdue_reason === 'failed' ? -1 : 0) - (b.overdue_reason === 'failed' ? -1 : 0) ||
  b.days_overdue - a.days_overdue ||
  (a.next_due || '').localeCompare(b.next_due || '');

export const PlanTable = ({ plans, onOpen }) => {
  const [filter, setFilter] = useState('All');
  const [q, setQ] = useState('');
  const [showDone, setShowDone] = useState(false);
  const match = (p) => !q || `${p.client_name} ${p.invoice_id}`.toLowerCase().includes(q.toLowerCase());
  const active = useMemo(() => plans.filter((p) => p.status !== 'Completed' && match(p) && (filter === 'All' || p.status === filter)).sort(sortPlans), [plans, filter, q]); // eslint-disable-line react-hooks/exhaustive-deps
  const done = plans.filter((p) => p.status === 'Completed' && match(p));

  return (
    <section className="rounded-2xl border border-[#E8E4DC] bg-white" data-testid="plan-register">
      <div className="flex flex-col gap-3 border-b border-[#F0ECE4] p-5 lg:flex-row lg:items-center lg:justify-between">
        <h2 className="font-heading text-xl font-semibold text-[#3A3530]">Payment plan register</h2>
        <div className="flex flex-wrap items-center gap-2">
          {FILTERS.map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`plan-filter-${slug(f)}`}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors duration-150 ${filter === f ? 'bg-[#3A3530] text-white' : 'bg-[#F5F1E8] text-[#6B6560] hover:bg-[#E8E4DC]'}`}>
              {f}
            </button>
          ))}
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-[#9B9590]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search client or invoice" className="h-9 w-56 border-[#E8E4DC] pl-8 text-sm" data-testid="plan-search-input" />
          </div>
        </div>
      </div>
      {active.length ? <Table rows={active} onOpen={onOpen} testId="active-plans-table" /> : (
        <p className="p-6 text-sm text-[#9B9590]" data-testid="plans-empty">No active plans match. Confirm a suggestion above or add one manually.</p>
      )}
      <Collapsible open={showDone} onOpenChange={setShowDone} className="border-t border-[#F0ECE4]">
        <CollapsibleTrigger className="flex w-full items-center justify-between px-5 py-3 text-sm font-medium text-[#6B6560] hover:bg-[#FDFBF7]" data-testid="completed-plans-toggle">
          <span>Completed plans · {done.length}</span>
          <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${showDone ? 'rotate-180' : ''}`} />
        </CollapsibleTrigger>
        <CollapsibleContent>
          {done.length ? <Table rows={done} onOpen={onOpen} testId="completed-plans-table" /> : <p className="px-5 pb-4 text-sm text-[#9B9590]">None yet.</p>}
        </CollapsibleContent>
      </Collapsible>
    </section>
  );
};
